import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PulpPaperRoute } from '../../types';

interface ProcessDetailData {
  corePrinciple: string;
  keyPoints: string[];
  technicalPoints: string[];
  comparison?: {
    title: string;
    headers: string[];
    rows: string[][];
  };
  notes?: string[];
}

const processDetailsMap: Record<string, ProcessDetailData> = {
  'mechanical-pulp': {
    corePrinciple: '机械法制浆利用机械旋转摩擦工作面处理木材等纤维原料\n通过摩擦产生的热量软化原料胞间层木素，使纤维分离\n得率达95%以上，不消耗化学药剂，生产成本最低',
    keyPoints: [
      'TMP是机械浆主流发展方向：浆物理强度比RMP有较大提高',
      'RTS技术：通过改变磨浆机速度(R)、磨浆温度(T)及浆料停留时间(S)来提高质量、降低能耗',
      '漂白：机械浆含木素高，漂白剂使用H₂O₂和Na₂S₂O₄，两段漂白可达白度~80%',
      '酶预处理：纤维素酶或木聚糖酶可降低TMP磨浆能耗',
      '高得率浆应用CSF范围：新闻纸100-150 mL，低定量铜版纸50-100 mL',
    ],
    technicalPoints: [
      '得率：~95%',
      '白度：~60%',
      'TMP蒸汽预热温度：<木素缩合温度',
      'RTS技术优化',
      '化机浆"90-90-9"目标：90%得率、90%白度、9 km裂断长',
    ],
    notes: [
      '纤维损伤大、物理强度较低',
      '浆中保留大量木素，难漂白且易返黄发脆',
      '能耗高于SGW 50%以上',
    ],
  },
  'chemical-pulp': {
    corePrinciple: '硫酸盐法制浆使用NaOH+Na₂S混合液(白液)在高温高压下蒸煮木片\n分解木质素使纤维离解\n碱回收系统：黑液→蒸发浓缩→燃烧回收→绿液苛化→白液回用',
    keyPoints: [
      '蒸煮温度160-170℃，时间~90min',
      '用碱量15-24%，硫化度22-35%',
      '两段硫酸盐法蒸煮：总用碱量降低2个百分点，卡伯值降低1.4个单位',
      '碱回收是核心配套：黑液干固物含量从<15%浓缩至>50%',
      '燃烧回收：1000-1200℃燃烧，有机物燃烧产能，无机物形成熔融物',
    ],
    technicalPoints: [
      '蒸煮温度：160-170℃',
      '蒸煮时间：~90 min',
      '用碱量：15-24%',
      '硫化度：22-35%',
      '细浆得率：45-55%',
    ],
    notes: [
      '纤维强度高，浆白度高',
      '原料适应广，碱回收成熟',
      '有臭气(TRS)，黑液需处理',
      '占化学浆~90%市场份额',
    ],
  },
  'chemimechanical-pulp': {
    corePrinciple: '化学预处理+机械磨解\n兼具化学浆和机械浆的优点：得率高(80-95%)、化学品消耗少、污染物发生量小\n主要类型：CTMP、BCTMP、APMP、P-RC APMP',
    keyPoints: [
      'CTMP：化学浸渍→反应仓(80-90℃, 30-60min)→多段压力磨浆(120-130℃)',
      'BCTMP：在CTMP基础上增加过氧化氢漂白段，白度可达66.5-73.5% ISO',
      'APMP：制浆与漂白合二为一，流程简化，废水无硫排放',
      'P-RC APMP：目前中国应用最广泛的化学机械浆技术',
      '清洁生产指标：单位产品取水量13-38 m³/Adt，综合能耗250-350 kgce/Adt',
    ],
    technicalPoints: [
      '得率：80-95%',
      '反应温度：80-90℃',
      '磨浆温度：120-130℃',
      '漂白白度：66.5-73.5% ISO',
      '浆白度：~50%(漂白前)',
    ],
    notes: [
      '得率高、强度高、污染小、能耗低',
      '自动化水平高，采用DCS集散控制系统',
      '代表项目：太阳纸业广西北海600吨/天，广西南宁450吨/天',
    ],
  },
  'deinked-pulp': {
    corePrinciple: '废纸脱墨是通过化学与物理结合的处理工艺，去除回收废纸纤维表面油墨\n核心是破坏油墨与纤维的黏附力，通过脱墨剂分离油墨并经后续处理清除残留',
    keyPoints: [
      '脱墨原理三步骤：疏解分离纤维→使油墨从纤维上脱离→把脱出油墨粒子从浆料中除去',
      '浮选法：油墨粒子随气泡上浮去除，适用较大粒子，全球占比>65%',
      '洗涤法：油墨乳化后随水洗去，适用小颗粒，北美占比90%',
      '酶法脱墨：化学品用量降低90%以上，COD/BOD降>50%，综合成本降低~50元/吨',
      '高档印刷纸脱墨浆：白度高、胶粘物和尘埃去除率高',
    ],
    technicalPoints: [
      '碎浆浓度：10-12%',
      '碎浆温度：55℃',
      '碎浆时间：40 min',
      '酶用量：0.03%',
      '脱墨浆ISO白度：可达93.42%',
    ],
    notes: [
      '资源循环利用，成本低，节能环保',
      '纤维强度下降，胶粘物难去除',
      '脱墨剂选择：洗涤脱墨要求乳化能力好，浮选脱墨要求分散性好',
    ],
  },
  'linerboard': {
    corePrinciple: '箱板纸是制造瓦楞纸箱的主要面纸\n牛皮箱板纸具有极高的抗压强度、耐戳穿强度与耐折度，防潮性能好\n多层结构：面层+芯层+底层',
    keyPoints: [
      '原料配比：国外标准100%本色硫酸盐木浆，国内常见40-50%木浆挂面',
      '成型方式：多长网成型，实现多层结构',
      '施胶：施胶度≥0.75mm，保证抗水防潮性能',
      '压光：机械压光机由若干冷铸铁辊组成，利用自重和附加压力使纸面平整',
      '纸机类型：长网多缸造纸机，车速可达800 m/min以上',
    ],
    technicalPoints: [
      '定量：140-300 g/m²',
      '施胶度：≥0.75mm',
      '车速：可达800 m/min以上',
      '耐破强度、环压强度(RCT)',
      '耐折度、施胶度',
    ],
    notes: [
      'A级：全木浆/竹浆，用于外包装及高档商品',
      'B级：部分木浆挂面，用于中档商品',
      'C级：废纸浆为主，用于一般包装',
    ],
  },
  'cultural-paper': {
    corePrinciple: '文化纸用于传播文化知识的书写、印刷纸张\n主要包括：未涂布印刷书写纸(双胶纸、书写纸)、涂布印刷纸(铜版纸)、新闻纸、轻量涂布纸(LWC)\n双胶纸是文化用纸中消费量最大的纸种，约占国内文化用纸消费量的40%',
    keyPoints: [
      '原料依赖：我国木浆消费量63%以上依赖进口，"林浆纸一体化"是行业趋势',
      '长短纤维配比：针叶木浆提供强度，阔叶木浆提供平滑度与不透明度',
      '填料添加：碳酸钙等提升白度、平滑度、不透明度，降低成本',
      '表面施胶：淀粉等施胶剂提升表面强度和印刷适性',
      '铜版纸涂布：表面涂布量通常15-25 g/m²/面，经超级压光整饰',
    ],
    technicalPoints: [
      '定量：60-90 g/m²(普通)',
      '白度：A等≥92%',
      '平滑度高',
      '光泽度、厚度均匀性',
      'pH值稳定性',
    ],
    notes: [
      '强周期性，传统旺季3-5月及9-11月(教材招标季)',
      '行业集中度高，铜版纸CR4约83%',
      '主要企业：太阳纸业、晨鸣纸业、华泰股份、博汇纸业、岳阳林纸等',
    ],
  },
  'white-cardboard': {
    corePrinciple: '白卡纸是以漂白木浆为基材的涂布包装材料\n经单面或双面涂布后压光整饰制成\n具有高挺度、高平滑度和良好印刷适性，可降解，满足食品级包装安全标准',
    keyPoints: [
      '多层结构抄造：面层、芯层、底层分别制浆，通过独立流浆箱进入长网成型器',
      '打浆度控制：面层/底层精磨至350-450 mL CSF',
      '涂布工艺：双面涂布，涂布量10-15 g/m²/面',
      '防伪技术：多层间歇喷淋工艺实现层间防伪图案，伪造成本提升300%+',
      '环保指标：白水循环率92%，吨纸清水耗量5.8 m³，优于国家标准15%',
    ],
    technicalPoints: [
      '定量：160-600 g/m²',
      '白度：A等≥92%',
      '涂布量：10-15 g/m²/面',
      '车速：可达1000 m/min以上',
      '食品级安全：迁移量测试(铅≤0.01 mg/kg)',
    ],
    notes: [
      '黄芯白卡(FBB)：厚度佳，机械性能好，用于普通小彩盒',
      '白芯白卡(SBS)：白度高，紧度大，用于香烟及高档化妆品',
      '无塑涂布型：采用生物基胶黏剂替代PE淋膜层，降解周期缩短至6-9个月',
    ],
  },
  'tissue-paper': {
    corePrinciple: '卫生纸是以原生木浆或回用浆为原料\n经打浆、抄造、起皱等工序制成的柔软吸水纸张\n起皱工艺是其区别于其他纸种的核心特征',
    keyPoints: [
      '打浆与帚化：纤维切断与"帚化"(细纤维化)使纤维变得柔软蓬松',
      '长短纤维配比：针叶木浆(长纤维)提供强度，阔叶木浆(短纤维)提供松软和吸水性',
      '起皱工艺(核心工序)：纸张在杨克烘缸上被刮刀刮出细小皱纹',
      '水溶性纤维：添加羧甲基纤维素钠(CMC)，与植物纤维组成纤维网，遇水迅速溶解',
      '成品加工：通常3-4层复合，平衡节省与防透',
    ],
    technicalPoints: [
      '起皱速比：1.2-1.3',
      '纸页干度：~93%',
      '刮刀角度：18-30°',
      '纵向伸长率提高8倍',
      '纸张厚度增加3倍',
    ],
    notes: [
      '起皱效果：纵向伸长率提高8倍，纸张厚度增加3倍，挺度降低38%',
      '纸张更有弹性、更厚、更能吸水、更柔软',
      '卫生纸掉粉量≤0.5%(自身重量)',
      '认准"原生木浆"四字即可保证品质',
    ],
  },
};

interface PulpPaperProcessDetailProps {
  route: PulpPaperRoute;
  onClose: () => void;
}

const PulpPaperProcessDetail: React.FC<PulpPaperProcessDetailProps> = ({ route, onClose }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const detailData = processDetailsMap[route.id];

  if (!detailData) {
    return (
      <Card sx={{ height: '100%', backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff' }}>
        <CardContent>
          <Typography variant="h6" color="text.secondary">
            暂无该工艺的详细信息
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h5"
            sx={{ color: isDarkMode ? '#66bb6a' : '#2e7d32', fontWeight: 600 }}
          >
            {route.name} - 工艺详情
          </Typography>
          <Tooltip title="关闭详情">
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 2, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: isDarkMode ? '#ffb74d' : '#ff9800',
              mb: 1,
            }}
          >
            📌 核心原理
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
            >
              {detailData.corePrinciple}
            </Typography>
          </Paper>
        </Box>

        {detailData.technicalPoints && detailData.technicalPoints.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#81d4fa' : '#0288d1',
                mb: 1,
              }}
            >
              ⚙️ 技术参数
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {detailData.technicalPoints.map((point, index) => (
                <Chip
                  key={index}
                  label={point}
                  size="small"
                  sx={{
                    backgroundColor: isDarkMode ? '#0d47a1' : '#e3f2fd',
                    color: isDarkMode ? '#90caf9' : '#1976d2',
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {detailData.keyPoints && detailData.keyPoints.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#ce93d8' : '#9c27b0',
                mb: 1,
              }}
            >
              🔑 关键技术点
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {detailData.keyPoints.map((point, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: isDarkMode ? '#4a148c' : '#f3e5f5',
                    borderRadius: 1,
                    borderLeft: '4px solid',
                    borderColor: isDarkMode ? '#ce93d8' : '#9c27b0',
                  }}
                >
                  <Typography variant="body2">{point}</Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {detailData.comparison && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#66bb6a' : '#2e7d32',
                mb: 1,
              }}
            >
              📊 {detailData.comparison.title}
            </Typography>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                backgroundColor: isDarkMode ? '#2a2a2a' : '#fafafa',
                border: '1px solid',
                borderColor: isDarkMode ? '#444444' : '#e0e0e0',
              }}
            >
              <Table size="small">
                <TableBody>
                  <TableRow sx={{ backgroundColor: isDarkMode ? '#1b5e20' : '#e8f5e9' }}>
                    {detailData.comparison.headers.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? '#66bb6a' : '#2e7d32',
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                  {detailData.comparison.rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {detailData.notes && detailData.notes.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#ffcc80' : '#ff9800',
                mb: 1,
              }}
            >
              💡 实践要点
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {detailData.notes.map((note, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    pl: 2,
                    borderLeft: '3px solid',
                    borderColor: isDarkMode ? '#ffcc80' : '#ff9800',
                  }}
                >
                  {note}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PulpPaperProcessDetail;
